/** Anzeigetexte der Pflege-App — echte Umlaute, an einer Stelle.
 *
 *  Spiegel von `hetzner/webhook/pflege_labels.py`. Die gespeicherten Werte sind
 *  bewusst ASCII (`stueck`, `jaehrlich`): sie stehen in Bestandsdaten und in
 *  Vergleichen, und ein Datenwert soll sich nicht ändern, nur weil jemand die
 *  Schreibweise schöner findet. Was ein Mensch liest, kommt aus diesen Tabellen.
 *
 *  Unbekannte Werte werden durchgereicht statt verschluckt: eine selbst
 *  getippte Einheit wie „Ballen" soll erscheinen, nicht verschwinden.
 */

export const EINHEIT_LABEL: Record<string, string> = {
  qm: "m²",
  cbm: "m³",
  lfm: "lfm",
  stueck: "Stück",
  std: "Std.",
  stunde: "Std.",
  pauschal: "pauschal",
  anfahrt: "Anfahrt",
  fuhre: "Fuhre",
  tag: "Tag",
  durchgang: "Durchgang",
  einsatz: "Einsatz",
};

export const ZAHLWEISE_LABEL: Record<string, string> = {
  monatlich: "Monatlich",
  quartalsweise: "Quartalsweise",
  halbjaehrlich: "Halbjährlich",
  jaehrlich: "Jährlich",
  je_einsatz: "Je Einsatz",
};

export const ZAHLUNGSART_LABEL: Record<string, string> = {
  ueberweisung: "Überweisung",
  lastschrift: "SEPA-Lastschrift",
  bar: "Barzahlung",
};

export const KATEGORIE_LABEL: Record<string, string> = {
  rasen: "Rasen",
  hecke: "Hecke",
  beet: "Beet",
  baum: "Bäume",
  unkraut: "Unkraut",
  laub: "Laub",
  duengung: "Düngung",
  bewaesserung: "Bewässerung",
  entsorgung: "Entsorgung",
  maschine: "Maschine",
  anfahrt: "Anfahrt",
  sonstiges: "Sonstiges",
};

function label(tabelle: Record<string, string>, wert: string | null | undefined): string {
  const roh = (wert ?? "").trim();
  return tabelle[roh.toLowerCase()] ?? roh;
}

export const einheitLabel = (w: string | null | undefined) => label(EINHEIT_LABEL, w);
export const zahlweiseLabel = (w: string | null | undefined) => label(ZAHLWEISE_LABEL, w);
export const zahlungsartLabel = (w: string | null | undefined) => label(ZAHLUNGSART_LABEL, w);
export const kategorieLabel = (w: string | null | undefined) => label(KATEGORIE_LABEL, w);

/** Anzeige → gespeicherter Wert. Damit darf ein Eingabefeld „Stück" zeigen und
 *  trotzdem `stueck` speichern; was in keiner Tabelle steht (eine selbst
 *  getippte Einheit), bleibt unverändert stehen. */
export function einheitWert(anzeige: string): string {
  const gesucht = (anzeige || "").trim();
  if (!gesucht) return "";
  const treffer = Object.entries(EINHEIT_LABEL)
    .find(([, label]) => label.toLowerCase() === gesucht.toLowerCase());
  return treffer ? treffer[0] : gesucht;
}

/** Vergleichsform für die Suche: Groß-/Kleinschreibung, Umlaute und ihre
 *  Umschreibungen fallen zusammen. „Grünschnitt", „gruenschnitt" und
 *  „GRUENSCHNITT" sind damit dasselbe Wort — sonst müsste beim Suchen die
 *  Schreibweise geraten werden. */
export function suchform(text: string): string {
  return (text || "")
    .toLowerCase()
    .replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/** Passt der Suchtext auf das Ziel? Alle eingegebenen Wortteile müssen
 *  vorkommen, Reihenfolge egal — „schnitt heck" findet den Heckenschnitt. */
export function passtAufSuche(ziel: string, suche: string): boolean {
  const s = suchform(suche);
  if (!s) return true;
  const z = suchform(ziel);
  return s.split(" ").every(teil => z.includes(teil));
}
